import { useEffect } from 'react';
import { create } from 'zustand';
import {
  fetchPublicProductById,
  fetchPublicProducts,
  findCachedProductById,
  invalidatePublicProductsCache,
  publicProductsCacheKey,
} from '../api/productsApi';
import { AuthApiError } from '../api/authApi';
import type { Product } from '../types';

export type CatalogStatus = 'idle' | 'loading' | 'success' | 'error';

export type CatalogSlice = {
  products: Product[];
  status: CatalogStatus;
  error: string | null;
};

const emptySlice = (): CatalogSlice => ({
  products: [],
  status: 'idle',
  error: null,
});

function catalogFetchCategory(category?: string): string | undefined {
  const t = category?.trim();
  if (!t || t === 'All') return undefined;
  return t;
}

interface ProductsStore {
  slices: Record<string, CatalogSlice>;
  productSlices: Record<
    string,
    { product: Product | null; status: CatalogStatus; error: string | null }
  >;
  prefetchCatalog: () => void;
  ensureCatalog: (category?: string) => Promise<Product[]>;
  ensureProduct: (id: string) => Promise<Product | null>;
  invalidate: () => void;
}

export const useProductsStore = create<ProductsStore>((set, get) => ({
  slices: {},
  productSlices: {},

  prefetchCatalog: () => {
    void get().ensureCatalog();
  },

  ensureCatalog: async (category?: string) => {
    const key = publicProductsCacheKey(category);
    const prev = get().slices[key] ?? emptySlice();
    const hasStaleData = prev.products.length > 0;

    if (!hasStaleData) {
      set((s) => ({
        slices: {
          ...s.slices,
          [key]: { ...prev, status: 'loading', error: null },
        },
      }));
    }

    try {
      const list = await fetchPublicProducts(catalogFetchCategory(category));
      set((s) => ({
        slices: {
          ...s.slices,
          [key]: { products: list, status: 'success', error: null },
        },
      }));
      return list;
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Could not load products.';
      set((s) => ({
        slices: {
          ...s.slices,
          [key]: {
            products: hasStaleData ? prev.products : [],
            status: 'error',
            error: msg,
          },
        },
      }));
      return hasStaleData ? prev.products : [];
    }
  },

  ensureProduct: async (id: string) => {
    const cached = findCachedProductById(id);
    const prev = get().productSlices[id];
    if (cached && !prev) {
      set((s) => ({
        productSlices: {
          ...s.productSlices,
          [id]: { product: cached, status: 'success', error: null },
        },
      }));
    }

    const current = get().productSlices[id];
    const hasProduct = Boolean(current?.product ?? cached);
    if (!hasProduct) {
      set((s) => ({
        productSlices: {
          ...s.productSlices,
          [id]: {
            product: current?.product ?? null,
            status: 'loading',
            error: null,
          },
        },
      }));
    }

    try {
      const p = await fetchPublicProductById(id);
      set((s) => ({
        productSlices: {
          ...s.productSlices,
          [id]: {
            product: p,
            status: 'success',
            error: p ? null : 'Product not found.',
          },
        },
      }));
      return p;
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Could not load product.';
      set((s) => ({
        productSlices: {
          ...s.productSlices,
          [id]: {
            product: (current?.product ?? cached) ?? null,
            status: 'error',
            error: msg,
          },
        },
      }));
      return (current?.product ?? cached) ?? null;
    }
  },

  invalidate: () => {
    invalidatePublicProductsCache();
    set({ slices: {}, productSlices: {} });
  },
}));

/** Catalog hook — shows cached products immediately, revalidates in the background. */
export function useCatalog(category?: string) {
  const key = publicProductsCacheKey(category);
  const slice = useProductsStore((s) => s.slices[key] ?? emptySlice());
  const ensureCatalog = useProductsStore((s) => s.ensureCatalog);

  useEffect(() => {
    void ensureCatalog(category);
  }, [category, ensureCatalog]);

  const loading = slice.status === 'loading' && slice.products.length === 0;
  return { products: slice.products, loading, error: slice.error, status: slice.status };
}

/** Single product — uses catalog cache when available. */
export function useProduct(id: string | undefined) {
  const slice = useProductsStore((s) =>
    id ? s.productSlices[id] ?? { product: null, status: 'idle' as const, error: null } : null,
  );
  const ensureProduct = useProductsStore((s) => s.ensureProduct);

  useEffect(() => {
    if (!id) return;
    void ensureProduct(id);
  }, [id, ensureProduct]);

  const loading = Boolean(id) && (slice?.status === 'loading' || slice?.status === 'idle') && !slice?.product;
  return {
    product: slice?.product ?? null,
    loading,
    error: slice?.error ?? null,
  };
}
